import React, { useEffect, useState, useCallback } from 'react'
import { Grid, Box, Paper, Typography, Chip, Tooltip } from "@mui/material"
import * as actions from "./actions"
import moment from "moment"
import { useNavigate, Link } from "react-router-dom"
import Pagination from '@mui/material/Pagination';
import _ from "lodash";
import SearchInput from '../../components/SearchInput';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import { API_URL } from '../../constants';
import { Music, Calendar, Disc, User, Mail, Tag, ExternalLink } from 'lucide-react';

export default function RejectedRelease(props) {

    const navigate = useNavigate();
    const [releases, setReleases] = useState([])
    const [filters, setFilters] = useState({
        sort: 'rejected',
        limit: 50,
        offset: 0,
        page: 1
    })
    const [count, setCount] = useState(0)
    const [search, setSearch] = useState('')
    const delaySearch = useCallback(_.debounce(filters => getSearchReleases(filters), 500), []);

    useEffect(() => {
        onGetNewReleases(filters)
    }, [filters])

    const onPageChange = (event, page) => {
        const offset = (page - 1) * filters.limit
        setFilters({ ...filters, page, offset })
    }

    const onGetNewReleases = (filters) => {
        props.setLoader(true)
        actions.getNewReleasesListAdmin(filters).then(res => {
            props.setLoader(false)
            setReleases(res.body)
            setCount(res.total)
        }).catch(err => {
            props.setLoader(false)
        })
    }

    const getSearchReleases = (filters) => {
        props.setLoader(true)
        actions.searchInReleases({ ...filters, type: 'rejected' }).then(res => {
            props.setLoader(false)
            setReleases(res.body)
        }).catch(err => {
            props.setLoader(false)
        })
    }

    const searchReleases = (value) => {
        setSearch(value)
        if (!value) {
            onGetNewReleases(filters)
        } else {
            delaySearch({ search: value })
        }
    }

    let totalPages = Math.ceil(Number(count) / 50)
    if (search) totalPages = 1

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
                    <Box sx={{ flex: 1, maxWidth: 420 }}>
                        <SearchInput value={search} onChange={(e) => searchReleases(e.target.value)} placeholder="Search by title, artist, or email..." />
                    </Box>
                    <Chip
                        icon={<Disc size={14} />}
                        label={`${count} Release${count !== 1 ? 's' : ''}`}
                        sx={{
                            backgroundColor: 'rgba(239, 68, 68, 0.12)',
                            color: '#EF4444',
                            border: '1px solid rgba(239, 68, 68, 0.25)',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            height: 34,
                            flexShrink: 0,
                            '& .MuiChip-icon': { color: '#EF4444' },
                        }}
                    />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box className="approved-release-list">
                    {releases.length ? releases.map((item, key) => (
                        <Link
                            key={item.release_id || key}
                            to={`/view_releases/${item.release_id}`}
                            state={{ from: '/release/rejected' }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                        <Paper
                            className="approved-release-card"
                        >
                            <div className="approved-release-card__artwork">
                                {item.album_artwork ? (
                                    <img
                                        src={`${API_URL}/${item.album_artwork}`}
                                        alt={item.release_title}
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                    />
                                ) : null}
                                <div className="approved-release-card__artwork-fallback" style={item.album_artwork ? { display: 'none' } : {}}>
                                    <Music size={20} color="#64748B" />
                                    <span>No image</span>
                                </div>
                            </div>

                            <div className="approved-release-card__body">
                                <div className="approved-release-card__top">
                                    <div className="approved-release-card__title-row">
                                        <Typography className="approved-release-card__title">
                                            {item.release_title || 'Untitled Release'}
                                        </Typography>
                                        <span className="approved-release-card__id">FM-RL-{item.release_id}</span>
                                    </div>
                                </div>

                                <div className="approved-release-card__meta">
                                    <Tooltip title="Artist" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <User size={13} />
                                            {item.name || '-'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Email" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <Mail size={13} />
                                            {item.email || '-'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Label" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <Tag size={13} />
                                            {item.lable_name || '-'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Release Date" arrow placement="top">
                                        <span className="approved-release-card__meta-item">
                                            <Calendar size={13} />
                                            {moment(Number(item.release_date)).format('DD MMM YYYY')}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Tracks" arrow placement="top">
                                        <span className="approved-release-card__meta-item approved-release-card__meta-item--tracks">
                                            <Music size={13} />
                                            {item.number_of_tracks || 0} track{item.number_of_tracks !== 1 ? 's' : ''}
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>

                            <Tooltip title="Open in new tab" arrow placement="top">
                                <div
                                    className="approved-release-card__new-tab"
                                    onClick={(e) => { e.preventDefault(); window.open(`/view_releases/${item.release_id}`, '_blank'); }}
                                >
                                    <ExternalLink size={14} />
                                </div>
                            </Tooltip>

                            <div className="approved-release-card__status">
                                <StatusBadge status={item.status} />
                            </div>

                            <div className="approved-release-card__arrow">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>
                        </Paper>
                        </Link>
                    )) : (
                        <Paper sx={{ p: 0 }}>
                            <table style={{ width: '100%' }}><tbody>
                                <EmptyState message="No Rejected Releases Found" colSpan={1} />
                            </tbody></table>
                        </Paper>
                    )}
                </Box>

                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={totalPages}
                            showFirstButton
                            showLastButton
                            onChange={onPageChange}
                            page={filters.page || 1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#94A3B8',
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(108, 99, 255, 0.2)',
                                        color: '#6C63FF',
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    )
}
